import { isKnownBpoSlug } from './bpoWizardSlugs';
/**
 * Remembers the last public page a visitor saw before opening a request
 * flow (/start, /contact, /booking, /quote), so every commercial CTA keeps
 * its source page and the consulted service without editing each link.
 */
const KEY = 'bpo_cta_source_v1';
const FLOW_RE = /^\/[a-z]{2}\/(start|contact|booking|quote)(\/|$)/;
const PRIVATE_RE = /^\/(admin|agent|[a-z]{2}\/(portal|auth|agent|admin))(\/|$)/;

export function isRequestFlowPath(path: string): boolean {
  return FLOW_RE.test(path);
}

/** Returns the path to remember, or null if this path must not overwrite it. */
export function sourceToRemember(path: string): string | null {
  if (!path || isRequestFlowPath(path) || PRIVATE_RE.test(path)) return null;
  return path.slice(0, 200);
}

export function rememberRoute(path: string): boolean {
  const p = sourceToRemember(path);
  if (!p) return false;
  try {
    sessionStorage.setItem(KEY, p);
    return true;
  } catch (err) {
    console.warn('[ctaSource] sessionStorage unavailable, source page not kept', err);
    return false;
  }
}

export function getCtaSource(): string | null {
  try { return sessionStorage.getItem(KEY); } catch { return null; }
}

/** Last path segment of the source page, used as a service hint (e.g. "technical-support"). */
export function serviceSlugFromSource(source: string | null): string | null {
  if (!source) return null;
  const seg = source.split('?')[0].split('/').filter(Boolean);
  if (seg.length < 2) return null;
  return seg[seg.length - 1].toLowerCase();
}
// Whole-word slug parts only: "appointment", "webinar" or "audit-" are not IT pages.
const IT_PAGE_RE = /(^|[/-])(digital|software|it|development|web|app|apps|mobile|saas|augmentation)([/-]|$)/;
/**
 * Explicit map from digital-studio page slugs to IT wizard category ids
 * (src/data/itPackages.ts). A slug absent from this map is ambiguous: no
 * category is preselected rather than guessing a wrong one.
 */
export const IT_SLUG_TO_CATEGORY: Record<string, string> = {
  'web-development': 'web-development',
  'mobile-app-development': 'mobile-apps',
  'mobile-development': 'mobile-apps',
  'custom-saas': 'saas-platforms',
  'ecommerce': 'ecommerce',
  'ui-ux-design': 'ui-ux-design',
  'devops-cloud-migration': 'devops-cloud',
  'ai-ml-development': 'ai-ml',
  'emerging-tech-ai': 'ai-ml',
  'digital-transformation': 'consulting',
};
const QUOTE_LABEL_RE = /quote|assessment|get started|devis|angebot|عرض|ابدأ/i;
/** Labels that ask for a service, a price or an order (not "talk to an expert"/"schedule a call"). */
const COMMERCIAL_LABEL_RE = /quote|pricing|price|order|hire|start|project|get started|request|devis|tarif|commander|demander|lancer|angebot|preis|bestellen|anfragen|presupuesto|precio|pedir|solicitar|عرض|اطلب|ابدأ|سعر/i;

/**
 * Single builder for every commercial CTA: opens the order widget with
 * language, order type, service hint (last page segment) and source page.
 * `pathname` is the current localized path (e.g. "/en/services/technical-support").
 */
const HUB_PAGES = new Set(['/services', '/services/bpo', '/services/digital-studio']);

/**
 * Adds the source (from) and the localized-free page path to an explicit
 * /start link, keeping its own type/service/package parameters.
 */
export function withSource(href: string, pathname: string, from: string): string {
  const [b, q = ''] = href.split('?');
  const qs = new URLSearchParams(q);
  const page = (pathname || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
  if (!qs.get('from')) qs.set('from', from);
  if (!qs.get('page')) qs.set('page', page);
  // Every conversion CTA carries a stable identifier; default to its source slot.
  if (!qs.get('cta_id')) qs.set('cta_id', qs.get('from') || from);
  return `${b}?${qs.toString()}`;
}

export function startHref(language: string, pathname: string, from: string, ctaId?: string): string {
  const page = (pathname || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
  // BPO service pages stay BPO even when a slug word looks digital (digital-engagement).
  const type = /^\/services\/bpo(\/|$)/.test(page) ? 'bpo' : IT_PAGE_RE.test(page) ? 'it' : 'bpo';
  const qs = new URLSearchParams({ type });
  const seg = page.split('/').filter(Boolean);
  // Hub pages (/services/bpo, /services/digital-studio) name a family, not a service.
  const hub = HUB_PAGES.has(page.replace(/\/$/, ''));
  // Industry pages carry a sector, not a service: never guess a service from them.
  if (seg[0] === 'industries') {
    if (seg.length >= 2) qs.set('industry', seg[1].toLowerCase().replace(/-support$/, ''));
  } else if (hub) {
    // no service hint from a hub page
  } else if (type === 'bpo' && seg.length >= 2 && seg[0] === 'services') qs.set('service', seg[seg.length - 1].toLowerCase());
  // INC-1910: call-center landing and region pages (/call-center, /call-center-europe) name the call-center service.
  else if (type === 'bpo' && seg.length === 1 && /^call-center(-|$)/.test(seg[0].toLowerCase())) qs.set('service', 'call-center');
  // UX-1910: the multilingual support landing names the documented multilingual service.
  else if (type === 'bpo' && seg.length === 1 && seg[0].toLowerCase() === 'multilingual-customer-support') qs.set('service', 'multilingual-support');
  // AUTO-1910: a /solutions/<slug> page names a service only when the wizard knows that slug.
  else if (type === 'bpo' && seg.length === 2 && seg[0] === 'solutions' && isKnownBpoSlug(seg[1])) qs.set('service', seg[1].toLowerCase());
  else if (type === 'it' && seg.length >= 2) {
    const cat = IT_SLUG_TO_CATEGORY[seg[seg.length - 1].toLowerCase()];
    if (cat) qs.set('service', cat);
  }
  qs.set('from', from);
  qs.set('page', page);
  qs.set('cta_id', ctaId || from);
  return `/${language}/start?${qs.toString()}`;
}

/**
 * BOOKING-CTX-3010: a /booking link from a public service page carries the same
 * service/from/page/cta_id as the /start builder; general pages, hubs and industry
 * pages keep a bare /booking. Explicit query values and a #fragment are preserved.
 */
export function bookingHref(language: string, pathname: string, from: string, explicit = '', ctaId?: string): string {
  const [q0, hash = ''] = explicit.split('#');
  const frag = hash ? `#${hash}` : '';
  const explicitQs = new URLSearchParams(q0);
  const service = new URLSearchParams(startHref(language, pathname, from).split('?')[1]).get('service');
  const qs = new URLSearchParams();
  if (service) {
    const page = (pathname || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/';
    qs.set('service', service);
    qs.set('from', from);
    qs.set('page', page);
    qs.set('cta_id', ctaId || from);
  }
  explicitQs.forEach((v, k) => { if (v) qs.set(k, v); });
  const q = qs.toString();
  return `/${language}/booking${q ? `?${q}` : ''}${frag}`;
}

/** A "/contact" related-link labelled as a quote/assessment request is commercial and belongs to /start. */
export function isCommercialContactLink(href: string, label: string): boolean {
  return href.split('?')[0] === '/contact' && QUOTE_LABEL_RE.test(label || '');
}

/**
 * Resolves a template CTA href: a bare "/start" (order widget) gets type,
 * service and source page from the current path; any other href is just
 * prefixed with the language.
 */
export function resolveCtaHref(language: string, pathname: string, href: string, from: string, label?: string): string {
  if (/^(https?:|mailto:|tel:|#)/.test(href)) return href;
  // Drop an already-present language prefix so it is never doubled (/en/en/contact).
  const rel = (href.startsWith('/') ? href : `/${href}`).replace(/^\/[a-z]{2}(?=\/|$|\?|#)/, '') || '/';
  // BOOKING-CTX-3010: a #fragment is split off first so "/booking#details" still matches /booking.
  const [beforeHash, hash = ''] = rel.split('#');
  const [path, query = ''] = beforeHash.split('?');
  // A primary slot (hero/CTA/deep-dive/pricing-teaser) pointing to /contact or /pricing
  // is a service request by position, whatever the label language — never leave it on /contact.
  const PRIMARY_SLOT_RE = /(hero-primary|cta-primary|deep-dive-cta|pricing-teaser)$/;
  // Explicit talk-to-a-human labels keep their contact intent even in a primary slot.
  const CONTACT_INTENT_RE = /expert|call|appel|discuter|discuss|écrire|message|talk|speak|book|réserver/i;
  // "View pricing" labels mean the pricing page, never the quote widget.
  const VIEW_PRICING_RE = /(voir|view|see|compare|comparer|ansehen|ver|bekijk)\b.*(tarif|pricing|prices|prix|preise|precios|prijzen)/i;
  const viewPricing = path === '/pricing' && VIEW_PRICING_RE.test(label || '');
  const commercial = !viewPricing && (path === '/contact' || path === '/pricing') && !CONTACT_INTENT_RE.test(label || '') && (COMMERCIAL_LABEL_RE.test(label || '') || PRIMARY_SLOT_RE.test(from));
  if (path === '/start' || commercial) {
    const base = startHref(language, pathname, from);
    if (!query) return base;
    // Explicit context in the template link (type/service/industry/channel) wins over the guess.
    const [b, bq] = base.split('?');
    const qs = new URLSearchParams(bq);
    new URLSearchParams(query).forEach((v, k) => {
      if (['type', 'service', 'industry', 'channel', 'cta_id'].includes(k) && v) qs.set(k, v);
    });
    return `${b}?${qs.toString()}`;
  }
  if (path === '/booking') return bookingHref(language, pathname, from, hash ? `${query}#${hash}` : query);
  return `/${language}${rel}`;
}

/**
 * Origin recorded on a lead: the link's own from= / page= parameters win over
 * the session fallback, so a /start visit from /call-center never reads "direct".
 * Only short slug/path values are kept (no free text).
 */
export function leadOriginFromSearch(search: string, fallback: string | null): string {
  const q = new URLSearchParams(search || '');
  const from = (q.get('from') || '').trim();
  const page = (q.get('page') || '').trim();
  const okFrom = /^[a-z0-9][a-z0-9_-]{0,60}$/i.test(from) ? from : '';
  const okPage = /^\/[a-z0-9/_-]{0,100}$/i.test(page) ? page : '';
  const base = okFrom || fallback || 'direct';
  return okPage ? `${base}|page:${okPage}` : base;
}
